import { useCallback, useMemo, type CSSProperties, type ReactNode } from "react";

import {
  createElementStateKey,
  useContainingElementState,
  useExistingOrNewNodeReferenceKey,
  useLayoutAncestry,
  type ElementStateConfig,
  type ElementStateKey,
} from "../element-state";
import type { LayoutSettings } from "../types.ts";
import { EditingCssPropertiesContext, type CombinedUpdate } from "./EditingCssPropertiesContext";
import { combineCssWithOverrides, formatOverridePropertiesForConfig } from "./transformCssProperties";

// Subset of LayoutSlotConfig and FlexboxContainerConfig
interface CssPropertiesNodeConfig {
  cssProperties: CSSProperties;
  layoutSettings: LayoutSettings;
  stylesByElementState?: Partial<Record<ElementStateKey, ElementStateConfig>>;
  nodeReferences?: Record<string, number>;
}

export type EditingCssPropertiesProviderProps = {
  id: number;
  config: CssPropertiesNodeConfig;
  onConfigChange: (config: Partial<CssPropertiesNodeConfig>) => void;
  children: ReactNode;
};

export const EditingCssPropertiesProvider = ({
  config,
  onConfigChange,
  children,
}: EditingCssPropertiesProviderProps) => {
  const editingElementState = useContainingElementState(useLayoutAncestry());
  const ancestry = useLayoutAncestry();
  const nodeReferenceKey = useExistingOrNewNodeReferenceKey(ancestry, editingElementState, config.nodeReferences);

  // Using elementStateId in context, we can apply styles to the element based on a parent's state with
  // the magic of emotion's & syntax.
  const cssPropertiesKey = useMemo(() => {
    if (editingElementState) {
      // note nodeReferenceKey is null if editing the selected node
      return createElementStateKey(editingElementState.elementState, nodeReferenceKey);
    }
    return null;
  }, [editingElementState, nodeReferenceKey]);

  // Merge base layout settings with element state overrides (partial merge)
  const layoutSettingsForSelectedState: LayoutSettings = useMemo(() => {
    if (cssPropertiesKey) {
      const overrides = config.stylesByElementState?.[cssPropertiesKey]?.layoutSettings;
      if (overrides) {
        return { ...config.layoutSettings, ...overrides };
      }
    }
    return config.layoutSettings;
  }, [cssPropertiesKey, config.layoutSettings, config.stylesByElementState]);

  // Designer side properties view uses this flattened cssProperties based on a singular element state.
  const cssPropertiesForSelectedState: CSSProperties = useMemo(
    () =>
      cssPropertiesKey
        ? combineCssWithOverrides(config.cssProperties, config.stylesByElementState?.[cssPropertiesKey]?.cssProperties)
        : config.cssProperties,
    [cssPropertiesKey, config.cssProperties, config.stylesByElementState],
  );

  const isEditingElementState = cssPropertiesKey !== null;

  /**
   * Core handler that updates CSS properties and/or layout settings atomically.
   * This is the single source of truth for config updates, preventing race conditions.
   */
  const handleChange = useCallback(
    ({ cssProperties: cssValues, layoutSettings: layoutValues }: CombinedUpdate) => {
      if (cssPropertiesKey) {
        // Compute CSS overrides
        const existingCssProperties = config.stylesByElementState?.[cssPropertiesKey]?.cssProperties;
        let overrideCssProperties = existingCssProperties ?? {};

        if (cssValues) {
          const combinedCssValues = combineCssWithOverrides(config.cssProperties, existingCssProperties);
          overrideCssProperties = formatOverridePropertiesForConfig(
            { ...combinedCssValues, ...cssValues },
            config.cssProperties,
          );
        }

        // Compute layout settings overrides
        let overrideLayoutSettings: Partial<LayoutSettings> | undefined =
          config.stylesByElementState?.[cssPropertiesKey]?.layoutSettings;

        if (layoutValues) {
          const mergedSettings = { ...layoutSettingsForSelectedState, ...layoutValues };
          const newOverrideSettings: Partial<LayoutSettings> = {};

          for (const key of Object.keys(mergedSettings) as (keyof LayoutSettings)[]) {
            if (mergedSettings[key] !== config.layoutSettings[key]) {
              (newOverrideSettings as Record<keyof LayoutSettings, LayoutSettings[keyof LayoutSettings]>)[key] =
                mergedSettings[key];
            }
          }

          overrideLayoutSettings = Object.keys(newOverrideSettings).length > 0 ? newOverrideSettings : undefined;
        }

        const newStylesByElementState = {
          ...config.stylesByElementState,
          [cssPropertiesKey]: {
            otherNodeRefKey: nodeReferenceKey ?? undefined,
            state: editingElementState!.elementState,
            cssProperties: overrideCssProperties,
            layoutSettings: overrideLayoutSettings,
          },
        };

        if (nodeReferenceKey) {
          onConfigChange({
            stylesByElementState: newStylesByElementState,
            nodeReferences: {
              ...config.nodeReferences,
              [nodeReferenceKey]: editingElementState!.nodeId,
            },
          });
        } else {
          onConfigChange({
            stylesByElementState: newStylesByElementState,
          });
        }
      } else {
        // Not editing element state - update base config
        onConfigChange({
          ...(cssValues ? { cssProperties: { ...config.cssProperties, ...cssValues } } : {}),
          ...(layoutValues ? { layoutSettings: { ...config.layoutSettings, ...layoutValues } } : {}),
        });
      }
    },
    [
      editingElementState,
      cssPropertiesKey,
      nodeReferenceKey,
      config.cssProperties,
      config.layoutSettings,
      config.stylesByElementState,
      config.nodeReferences,
      layoutSettingsForSelectedState,
      onConfigChange,
    ],
  );

  const handleCssPropertiesChange = useCallback(
    (values: CSSProperties) => handleChange({ cssProperties: values }),
    [handleChange],
  );

  const handleLayoutSettingsChange = useCallback(
    (values: Partial<LayoutSettings>) => handleChange({ layoutSettings: values }),
    [handleChange],
  );

  return (
    <EditingCssPropertiesContext.Provider
      value={{
        cssProperties: cssPropertiesForSelectedState,
        onCssPropertiesChange: handleCssPropertiesChange,
        layoutSettings: layoutSettingsForSelectedState,
        onLayoutSettingsChange: handleLayoutSettingsChange,
        onChange: handleChange,
        isEditingElementState,
      }}
    >
      {children}
    </EditingCssPropertiesContext.Provider>
  );
};
